import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { ClaimHelpPanel } from "@/components/ClaimHelpPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Hash, MessageCircle, Users } from "lucide-react";

export default function Messages() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isStaff = user && (user.role === "host" || user.role === "admin");
  const [tab, setTab] = useState("client_host");
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState({});
  const bottomRef = useRef(null);

  const loadUnread = useCallback(() => {
    api.get("/messages/unread").then(({ data }) => setUnread(data.by_conversation || {})).catch(() => {});
  }, []);

  const loadConvos = useCallback(async (type) => {
    const { data } = await api.get("/conversations", { params: { type: type === "team" ? "team" : "client_host" } });
    setConvos(data);
    setActive((prev) => prev && data.find((c) => c.conversation_id === prev.conversation_id) ? prev : data[0] || null);
    loadUnread();
  }, [loadUnread]);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.role) { navigate("/connexion"); return; }
    loadConvos(tab);
  }, [user, loading, navigate, tab, loadConvos]);

  const loadMessages = useCallback(async () => {
    if (!active) { setMessages([]); return; }
    try {
      const { data } = await api.get(`/conversations/${active.conversation_id}/messages`);
      setMessages(data);
    } catch { setMessages([]); }
  }, [active]);

  const openConversation = async (c) => {
    setActive(c);
    try { await api.post(`/conversations/${c.conversation_id}/read`); loadUnread(); } catch {}
  };

  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, 4000);
    return () => clearInterval(t);
  }, [loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post(`/conversations/${active.conversation_id}/messages`, { content: text });
      setText("");
      loadMessages();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!user || !user.role) return <div className="min-h-screen bg-background"><Header /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
        <h1 className="font-serif text-4xl text-olive mb-1">Messagerie</h1>
        <p className="text-muted-foreground mb-6">Échangez avec les clients, les hôtes et votre équipe</p>

        {isStaff && (
          <div className="inline-flex rounded-full bg-secondary p-1 mb-6">
            <button onClick={() => setTab("client_host")} className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "client_host" ? "bg-white text-olive shadow" : "text-muted-foreground"}`} data-testid="chat-tab-client-host">
              <MessageCircle className="w-4 h-4 inline mr-1.5" /> Clients ↔ Hôtes
            </button>
            <button onClick={() => setTab("team")} className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "team" ? "bg-white text-olive shadow" : "text-muted-foreground"}`} data-testid="chat-tab-team-internal">
              <Users className="w-4 h-4 inline mr-1.5" /> Équipe interne
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[560px]">
          <div className="rounded-2xl bg-white border border-border overflow-y-auto" data-testid="conversation-list">
            {convos.length === 0 && <p className="p-5 text-sm text-muted-foreground">Aucune conversation.</p>}
            {convos.map((c) => {
              const title = tab === "team" ? c.subject : (c.participant_names ? Object.entries(c.participant_names).filter(([id]) => id !== user.user_id).map(([, n]) => n).join(", ") : c.subject);
              return (
                <button key={c.conversation_id} onClick={() => openConversation(c)}
                  className={`w-full text-left px-4 py-3 border-b border-border transition-colors ${active?.conversation_id === c.conversation_id ? "bg-rosebrand-pale" : "hover:bg-secondary"}`}
                  data-testid={`conversation-${c.conversation_id}`}>
                  <p className="font-semibold text-olive flex items-center gap-1.5">
                    {tab === "team" ? <Hash className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
                    {tab === "team" ? c.subject?.replace("#", "") : title}
                    {unread[c.conversation_id] > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rose-brand text-white text-[10px] font-bold flex items-center justify-center" data-testid={`conv-unread-${c.conversation_id}`}>
                        {unread[c.conversation_id]}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{c.last_message || c.subject || c.description}</p>
                </button>
              );
            })}
          </div>

          <div className="md:col-span-2 rounded-2xl bg-white border border-border flex flex-col">
            {active ? (
              <>
                <div className="px-5 py-3 border-b border-border">
                  <p className="font-serif text-lg text-olive">{tab === "team" ? active.subject : (active.subject || "Conversation")}</p>
                </div>
                {isStaff && tab === "client_host" && <ClaimHelpPanel conversation={active} messages={messages} me={user} />}
                <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="messages-pane">
                  {messages.length === 0 && <p className="text-sm text-muted-foreground text-center mt-10">Aucun message. Démarrez la conversation !</p>}
                  {messages.map((m) => {
                    const mine = m.sender_id === user.user_id;
                    return (
                      <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-olive text-white" : "bg-secondary text-foreground"}`}>
                          {!mine && <p className="text-xs font-semibold text-rose-brand mb-0.5">{m.sender_name}</p>}
                          <p className="text-sm whitespace-pre-line">{m.content}</p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre message..." className="flex-1" data-testid="message-input" />
                  <Button type="submit" className="bg-rose-brand hover:bg-rosebrand-soft text-white" data-testid="send-message-button"><Send className="w-4 h-4" /></Button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">Sélectionnez une conversation</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
